import { formatDate } from '@backoffice-frontend/common'
import { SELECTED_FLEET_ID } from '@backoffice-frontend/service-area-picker'
import { addDays, set, startOfDay } from 'date-fns'
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'
import type { TFunction } from 'i18next'
import { createSerializer, parseAsString } from 'nuqs'
import { FleetTracingAreaRoutes } from '../../../FleetTracingAreaRoutes'

export enum VehicleIdentifierOptions {
  Label = 'label',
  VIN = 'vin',
  LicensePlate = 'licensePlate',
  UUID = 'uuid',
}

export type SearchFormValues = {
  regionId: string
  fleetId: string
  vehicleIdentifier: VehicleIdentifierOptions
  identifierValue: string
  startDateTime: Date | null
  endDateTime: Date | null
}

/**
 * - dateBefore: 01.11.2024, the start of AD data availability
 * - dateAfter: the end of yesterday, in the fleet's timezone
 */
export const getAllowedDateRange = (timeZoneId: string) => {
  const dateBefore = new Date(2024, 10, 1)

  const dateAfter = toZonedTime(new Date(), timeZoneId)
  dateAfter.setDate(dateAfter.getDate() - 1)
  dateAfter.setHours(23, 59, 59, 999)

  return { dateBefore, dateAfter }
}

export const VALID_VEHICLE_IDENTIFIER_VALUES = Object.values(
  VehicleIdentifierOptions,
)

export const formatUTCISO = (date: Date): string =>
  formatInTimeZone(date, 'UTC', "yyyy-MM-dd'T'HH:mm:ss")

// `URLSearchParams` percent-encodes the colons in the timestamps while `useQueryState`
// leaves them readable, which made the same search render two different URLs. Serializing
// with nuqs keeps the result page URL identical to the one the search page wrote.
const serializeVehicleSearchParams = createSerializer({
  region_id: parseAsString,
  [SELECTED_FLEET_ID]: parseAsString,
  vehicleIdentifier: parseAsString,
  identifierValue: parseAsString,
  startDateTime: parseAsString,
  endDateTime: parseAsString,
})

export type VehicleResultRouteParams = {
  regionId: string
  fleetId: string
  vehicleIdentifier: VehicleIdentifierOptions
  identifierValue: string
  startDateTime: Date
  endDateTime: Date
}

/** Builds the vehicle result route for a submitted search. */
export const buildVehicleResultRoute = ({
  regionId,
  fleetId,
  vehicleIdentifier,
  identifierValue,
  startDateTime,
  endDateTime,
}: VehicleResultRouteParams): string =>
  serializeVehicleSearchParams(FleetTracingAreaRoutes.Vehicle.route, {
    region_id: regionId,
    [SELECTED_FLEET_ID]: fleetId,
    vehicleIdentifier,
    identifierValue,
    startDateTime: formatUTCISO(startDateTime),
    endDateTime: formatUTCISO(endDateTime),
  })

// The pickers have minute precision, so the last selectable minute of the day is 23:59.
const DAY_END_WALL_CLOCK = {
  hours: 23,
  minutes: 59,
  seconds: 0,
  milliseconds: 0,
}

/**
 * Builds the full-day search window for the day `startDateTime` falls on:
 * 00:00 until 23:59 wall-clock in `timeZoneId`, returned as absolute instants.
 */
export const getFullDaySearchWindow = (
  startDateTime: Date,
  timeZoneId: string,
): { startDateTime: Date; endDateTime: Date } => {
  const zonedDayStart = startOfDay(toZonedTime(startDateTime, timeZoneId))

  return {
    startDateTime: fromZonedTime(zonedDayStart, timeZoneId),
    endDateTime: fromZonedTime(
      set(zonedDayStart, DAY_END_WALL_CLOCK),
      timeZoneId,
    ),
  }
}

/**
 * Compares two absolute instants by the calendar day they fall on in `timeZoneId`.
 * Returns false if either instant is missing.
 */
export const isSameZonedDay = (
  a: Date | null | undefined,
  b: Date | null | undefined,
  timeZoneId: string,
): boolean =>
  !!a &&
  !!b &&
  formatInTimeZone(a, timeZoneId, 'yyyy-MM-dd') ===
    formatInTimeZone(b, timeZoneId, 'yyyy-MM-dd')

// Validation helpers
const MINUTES_15 = 15 * 60 * 1000

type FormValidator = (
  _value: Date | null,
  formValues: SearchFormValues,
) => boolean | string

// react-hook-form validate signature is (fieldValue, allFormValues) — _value is positional
export const validateWindowMin15Min =
  (t: TFunction): FormValidator =>
  (_value, formValues) => {
    const { startDateTime, endDateTime } = formValues
    if (!startDateTime || !endDateTime) return true

    const duration = endDateTime.getTime() - startDateTime.getTime()
    if (duration < MINUTES_15) {
      return t('fleetTracing_validation_windowMin15Min')
    }

    return true
  }

export const validateWindowMax24h =
  (t: TFunction, timeZoneId: string): FormValidator =>
  (_value, formValues) => {
    const { startDateTime, endDateTime } = formValues
    if (!startDateTime || !endDateTime) return true

    // Advance 1 calendar day in the fleet timezone so DST transitions are respected:
    // fallback days allow 25 absolute hours, spring-forward days allow 23.
    const zonedStart = toZonedTime(startDateTime, timeZoneId)
    const maxAllowedEndDateTime = fromZonedTime(
      addDays(zonedStart, 1),
      timeZoneId,
    )

    if (endDateTime > maxAllowedEndDateTime) {
      return t('fleetTracing_validation_windowMax24h')
    }

    return true
  }

export const validateEndAfterStart =
  (t: TFunction): FormValidator =>
  (_value, formValues) => {
    const { startDateTime, endDateTime } = formValues
    if (!startDateTime || !endDateTime) return true

    if (endDateTime < startDateTime) {
      return t('fleetTracing_validation_endAfterStart')
    }

    return true
  }

// The datepicker only disables clicking on out-of-range days, typing a date bypasses it.
// `value` is the field's absolute-instant Date, so the range must be converted from the
// picker's zoned wall-clock domain into absolute instants before comparing.
export const validateDateWithinAllowedRange =
  (t: TFunction, timeZoneId: string): FormValidator =>
  value => {
    if (!value) return true

    const { dateBefore, dateAfter } = getAllowedDateRange(timeZoneId)
    const instantBefore = fromZonedTime(dateBefore, timeZoneId)
    const instantAfter = fromZonedTime(dateAfter, timeZoneId)

    if (value > instantAfter) {
      return t('fleetTracing_validation_selectDateInPast')
    }

    if (value < instantBefore) {
      return t('fleetTracing_validation_selectDateAfter', {
        date: formatDate(dateBefore),
      })
    }

    return true
  }
